import React, { useEffect, useState } from 'react';

import { ContainedButton, OutlinedButton } from '../../../../components/modules/Buttons';
import type { ComposerWebPreferences } from '../../../../helpers';
import {
  getComposerWebPreferences,
  isMobilePreferencesEnabled,
  setComposerWebPreferences,
} from '../../../../helpers';
import {
  type EmailNotificationSettings,
  getEmailNotificationSettings,
  getUserSettingsByPlatform,
  patchEmailNotificationSettings,
  patchUserSettingsByPlatform,
} from '../../../../api/usersApi';
import Modal from '../../../../components/modules/Modal';
import styles from './PreferencesModal.module.scss';

type TPreferencesModal = {
  open: boolean;
  onClose: () => void;
};

type TNumberPreferences = Pick<
  ComposerWebPreferences,
  'clipDefaultInSeconds' | 'clipDefaultOutSeconds' | 'playingArrowStepSeconds'
>;

const defaultEmailSettings: EmailNotificationSettings = {
  shared_content_email_notifications_enabled: true,
  moved_content_email_notifications_enabled: true,
};

const PreferencesModal: React.FC<TPreferencesModal> = ({ open, onClose }) => {
  const [webPreferences, setWebPreferences] = useState<ComposerWebPreferences>(
    getComposerWebPreferences(),
  );
  const [mobilePreferences, setMobilePreferences] = useState<ComposerWebPreferences>(
    getComposerWebPreferences(),
  );
  const [platform, setPlatform] = useState<'web' | 'mobile'>('web');
  const [emailSettings, setEmailSettings] = useState(defaultEmailSettings);
  const [loading, setLoading] = useState(false);
  const [numberPreferences, setNumberPreferences] = useState<Record<keyof TNumberPreferences, string>>({
    clipDefaultInSeconds: `${webPreferences.clipDefaultInSeconds}`,
    clipDefaultOutSeconds: `${webPreferences.clipDefaultOutSeconds}`,
    playingArrowStepSeconds: `${webPreferences.playingArrowStepSeconds}`,
  });

  const showMobilePreferences = isMobilePreferencesEnabled();
  const preferences = platform === 'web' ? webPreferences : mobilePreferences;

  useEffect(() => {
    if (open) {
      setLoading(true);
      const requests: [
        Promise<ComposerWebPreferences>,
        Promise<ComposerWebPreferences | null>,
        Promise<EmailNotificationSettings>,
      ] = [
        getUserSettingsByPlatform('web'),
        showMobilePreferences ? getUserSettingsByPlatform('mobile') : Promise.resolve(null),
        getEmailNotificationSettings(),
      ];
      Promise.all(requests)
        .then(([web, mobile, email]) => {
          const nextWebPreferences = {
            ...getComposerWebPreferences(),
            ...web,
          };
          const nextMobilePreferences = {
            ...getComposerWebPreferences(),
            ...(mobile || {}),
          };
          setWebPreferences(nextWebPreferences);
          setEmailSettings(email);
          if (showMobilePreferences) {
            setMobilePreferences(nextMobilePreferences);
          }
          setComposerWebPreferences(nextWebPreferences);
          const source = platform === 'web' ? nextWebPreferences : nextMobilePreferences;
          setNumberPreferences({
            clipDefaultInSeconds: `${source.clipDefaultInSeconds}`,
            clipDefaultOutSeconds: `${source.clipDefaultOutSeconds}`,
            playingArrowStepSeconds: `${source.playingArrowStepSeconds}`,
          });
        })
        .finally(() => setLoading(false));
    }
  }, [open, showMobilePreferences]);

  useEffect(() => {
    if (!showMobilePreferences && platform === 'mobile') {
      setPlatform('web');
    }
  }, [showMobilePreferences, platform]);

  useEffect(() => {
    if (!open) return;
    const source = platform === 'web' ? webPreferences : mobilePreferences;
    setNumberPreferences({
      clipDefaultInSeconds: `${source.clipDefaultInSeconds}`,
      clipDefaultOutSeconds: `${source.clipDefaultOutSeconds}`,
      playingArrowStepSeconds: `${source.playingArrowStepSeconds}`,
    });
  }, [
    platform,
    open,
    webPreferences.clipDefaultInSeconds,
    webPreferences.clipDefaultOutSeconds,
    webPreferences.playingArrowStepSeconds,
    mobilePreferences.clipDefaultInSeconds,
    mobilePreferences.clipDefaultOutSeconds,
    mobilePreferences.playingArrowStepSeconds,
  ]);

  const updateNumber = (
    key: keyof TNumberPreferences,
    value: string,
  ) => {
    setNumberPreferences((prev) => ({ ...prev, [key]: value }));
  };

  const setPreferences = (updater: (prev: ComposerWebPreferences) => ComposerWebPreferences) => {
    if (platform === 'web') {
      setWebPreferences((prev) => updater(prev));
    } else {
      setMobilePreferences((prev) => updater(prev));
    }
  };

  const onSync = async () => {
    if (!showMobilePreferences) return;
    setLoading(true);
    try {
      const sourceSettings = platform === 'web' ? webPreferences : mobilePreferences;
      const next = await patchUserSettingsByPlatform(platform, {
        ...sourceSettings,
        syncToOtherPlatform: true,
      });
      if (platform === 'web') {
        setWebPreferences(next);
        setMobilePreferences(next);
        setComposerWebPreferences(next);
      } else {
        setMobilePreferences(next);
        setWebPreferences(next);
        setComposerWebPreferences(next);
      }
    } finally {
      setLoading(false);
    }
  };

  const clipDefaultInSeconds = Math.max(0, Number(numberPreferences.clipDefaultInSeconds) || 0);
  const clipDefaultOutSeconds = Math.max(0, Number(numberPreferences.clipDefaultOutSeconds) || 0);
  const playingArrowStepSeconds = Number(numberPreferences.playingArrowStepSeconds);
  const canSave =
    clipDefaultInSeconds + clipDefaultOutSeconds >= 1 &&
    Number.isFinite(playingArrowStepSeconds) &&
    playingArrowStepSeconds > 0;

  const onSave = async () => {
    if (!canSave) {
      return;
    }

    const payload = {
      ...preferences,
      clipDefaultInSeconds,
      clipDefaultOutSeconds,
      playingArrowStepSeconds,
    };
    setLoading(true);
    try {
      const [next] = await Promise.all([
        patchUserSettingsByPlatform(platform, payload),
        patchEmailNotificationSettings(emailSettings),
      ]);
      if (platform === 'web') {
        setWebPreferences(next);
        setComposerWebPreferences(next);
      } else {
        setMobilePreferences(next);
      }
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const platformLabel = platform === 'web' ? 'Web' : 'Mobile';

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.title}>Preferences{showMobilePreferences ? ` (${platformLabel})` : ''}</div>
        {showMobilePreferences && (
          <>
            <div className={styles.segmented}>
              <button
                type="button"
                className={platform === 'web' ? styles.active : ''}
                onClick={() => setPlatform('web')}
              >
                Web
              </button>
              <button
                type="button"
                className={platform === 'mobile' ? styles.active : ''}
                onClick={() => setPlatform('mobile')}
              >
                Mobile
              </button>
            </div>
            <div className={styles.actions} style={{ marginBottom: '12px' }}>
              <OutlinedButton onClick={onSync}>Sync {platformLabel} to {platform === 'web' ? 'Mobile' : 'Web'}</OutlinedButton>
            </div>
          </>
        )}

        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={preferences.muteAllVideos}
            onChange={(event) =>
              setPreferences((prev) => ({
                ...prev,
                muteAllVideos: event.target.checked,
              }))
            }
          />
          <span>Mute all Videos</span>
        </label>

        {platform === 'web' && <label className={styles.checkboxRow}>
          <input type="checkbox" checked={preferences.showVideoDrawingsWhenPlayingClips}
            onChange={(event) => setPreferences((prev) => ({ ...prev, showVideoDrawingsWhenPlayingClips: event.target.checked }))} />
          <span>Show Video Drawings when playing Clips</span>
        </label>}

        <div className={styles.section}>
          <div className={styles.label}>Email notifications</div>
          <div className={styles.hint}>
            Choose which content activity emails you receive. These settings apply across all organisations.
          </div>
          <div className={styles.emailOptions}>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={emailSettings.shared_content_email_notifications_enabled}
                onChange={(event) =>
                  setEmailSettings((prev) => ({
                    ...prev,
                    shared_content_email_notifications_enabled: event.target.checked,
                  }))
                }
              />
              <span>Shared content</span>
            </label>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={emailSettings.moved_content_email_notifications_enabled}
                onChange={(event) =>
                  setEmailSettings((prev) => ({
                    ...prev,
                    moved_content_email_notifications_enabled: event.target.checked,
                  }))
                }
              />
              <span>Moved shared and collaborative content</span>
            </label>
          </div>
        </div>

        {platform === 'web' && (
          <div className={styles.section}>
            <div className={styles.label}>Allow Downloads by default</div>
            <div className={styles.hint}>
              Set the default for new shares. You can then override it for individual user roles.
            </div>
            <div className={styles.emailOptions}>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={preferences.shareAllowDownloadsDefault}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setPreferences((prev) => ({
                      ...prev,
                      shareAllowDownloadsDefault: checked,
                      shareAllowDownloadsDefaultOrganizationAdmin: checked,
                      shareAllowDownloadsDefaultAnalyst: checked,
                      shareAllowDownloadsDefaultViewer: checked,
                    }));
                  }}
                />
                <span>All users, groups and organisations</span>
              </label>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={preferences.shareAllowDownloadsDefaultOrganizationAdmin}
                  onChange={(event) =>
                    setPreferences((prev) => ({
                      ...prev,
                      shareAllowDownloadsDefaultOrganizationAdmin: event.target.checked,
                    }))
                  }
                />
                <span>Organization Admins</span>
              </label>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={preferences.shareAllowDownloadsDefaultAnalyst}
                  onChange={(event) =>
                    setPreferences((prev) => ({
                      ...prev,
                      shareAllowDownloadsDefaultAnalyst: event.target.checked,
                    }))
                  }
                />
                <span>Analysts</span>
              </label>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={preferences.shareAllowDownloadsDefaultViewer}
                  onChange={(event) =>
                    setPreferences((prev) => ({
                      ...prev,
                      shareAllowDownloadsDefaultViewer: event.target.checked,
                    }))
                  }
                />
                <span>Viewers</span>
              </label>
            </div>
          </div>
        )}

        <div className={styles.section}>
          <div className={styles.label}>Send Clips default position</div>
          <div className={styles.segmented}>
            <button
              type="button"
              className={!preferences.sendClipsAddOnBottom ? styles.active : ''}
              onClick={() =>
                setPreferences((prev) => ({
                  ...prev,
                  sendClipsAddOnBottom: false,
                }))
              }
            >
              Top
            </button>
            <button
              type="button"
              className={preferences.sendClipsAddOnBottom ? styles.active : ''}
              onClick={() =>
                setPreferences((prev) => ({
                  ...prev,
                  sendClipsAddOnBottom: true,
                }))
              }
            >
              Bottom
            </button>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.label}>Left/Right jump while playing</div>
          <div className={styles.hint}>
            When video is playing, left and right arrow keys jump by this many seconds. Paused arrows still move frame by frame.
          </div>
          <div className={styles.singleInput}>
            <label>
              <span>Jump seconds</span>
              <div className={styles.inputWithUnit}>
                <input
                  type="number"
                  min="0.1"
                  step="0.5"
                  value={numberPreferences.playingArrowStepSeconds}
                  onFocus={(event) => event.target.select()}
                  onChange={(event) =>
                    updateNumber('playingArrowStepSeconds', event.target.value)
                  }
                />
                <span>sec</span>
              </div>
            </label>
          </div>
          {(!Number.isFinite(playingArrowStepSeconds) || playingArrowStepSeconds <= 0) && (
            <div className={styles.error}>Jump seconds must be greater than 0.</div>
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.label}>New clip default range</div>
          <div className={styles.hint}>
            New clips will start {clipDefaultInSeconds}s before and end {clipDefaultOutSeconds}s after the point you choose, where space allows.
          </div>
          <div className={styles.timeGrid}>
            <label>
              <span>Before point</span>
              <div className={styles.inputWithUnit}>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={numberPreferences.clipDefaultInSeconds}
                  onFocus={(event) => event.target.select()}
                  onChange={(event) =>
                    updateNumber('clipDefaultInSeconds', event.target.value)
                  }
                />
                <span>sec</span>
              </div>
            </label>
            <label>
              <span>After point</span>
              <div className={styles.inputWithUnit}>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={numberPreferences.clipDefaultOutSeconds}
                  onFocus={(event) => event.target.select()}
                  onChange={(event) =>
                    updateNumber('clipDefaultOutSeconds', event.target.value)
                  }
                />
                <span>sec</span>
              </div>
            </label>
          </div>
          {!canSave && (
            <div className={styles.error}>Total clip length must be at least 1 second.</div>
          )}
        </div>

        <div className={styles.actions}>
          <ContainedButton onClick={onSave} disabled={!canSave}>
            Save
          </ContainedButton>
          <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
        </div>
      </div>
    </Modal>
  );
};

export default PreferencesModal;
